// cspell:disable
import { CarrierBillingAccountType } from "@redotech/cockroachdb-codegen/congodb/carrier-billing-account-type";
import { Money, moneyAdd } from "@redotech/money";
import { RateSource } from "@redotech/money/rate-source";
import { useRequiredContext } from "@redotech/react-util/context";
import { billingAccountType } from "@redotech/redo-fulfillment-carrier-account-common/carrier-billing-accounts";
import { getCarrierServiceLabel } from "@redotech/redo-fulfillment-carrier-common/carrier-service-labels";
import {
  carrierToLabel,
  carrierToTrademarkText,
} from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import {
  KnownSCAC,
  knownScacToLabel,
  supportedFreightMarketplaces,
} from "@redotech/redo-fulfillment-carrier-common/freight";
import {
  RedoShopifyDeliveryMethodType,
  RedoShopifyDeliveryMethodTypeToString,
} from "@redotech/redo-fulfillment-group-common/fulfillment-delivery-method-type";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { isDoneStatus } from "@redotech/redo-fulfillment-group-common/fulfillment-group-status";
import {
  getBillingAccountId,
  isExternalAccountBilling,
  RateProvider,
} from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import LocalPickupLogo from "@redotech/redo-icons/building-01.svg";
import CubeIcon from "@redotech/redo-icons/cube-01.svg";
import HelpCircleIcon from "@redotech/redo-icons/help-circle.svg";
import ScalesIcon from "@redotech/redo-icons/scales-01.svg";
import TiktokIcon from "@redotech/redo-icons/tiktok-color-icon.svg";
import TruckIcon from "@redotech/redo-icons/truck-02.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import { RedoSingleSelectDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { getFreightLogoUrl } from "@redotech/redo-web/freight/logo";
import {
  LogoSize,
  ShippingLogo,
} from "@redotech/redo-web/fulfillment/common/shipping-logo";
import { MoneyText } from "@redotech/redo-web/fx/money-text";
import { SkeletonText } from "@redotech/redo-web/skeleton";
import { Text } from "@redotech/redo-web/text";
import { formatShortDayDate } from "@redotech/redo-web/time";
import { OverflowTooltip } from "@redotech/redo-web/tooltip/overflow-tooltip";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { isDef } from "@redotech/util/checks";

import {
  getLcgInfo,
  landedCostMajorUnits,
  LcgInfo,
} from "@redotech/redo-fulfillment-operations-common/shipping/lcg-info";
import { toMoney } from "@redotech/redo-web/fx/to-money";
import { memo, useContext, useMemo, useState } from "react";
import { getShippingSelectionCellData } from "../fulfillment-groups/cells/shipping-selection-cell";
import { AutomationInfoIcon } from "./automation-info-icon";
import { CarrierAccountsContext } from "./carrier-accounts-context";
import { LearningIcon } from "./learning-icon";
import {
  PinVerificationType,
  usePinVerification,
} from "./pin-verification-context";
import * as styles from "./shipment-rate-selection-dropdown.module.css";
import FlowArrowIcon from "./simple-rate-tooltip/flow-arrow.svg";
import PackageBoxIcon from "./simple-rate-tooltip/package-box.svg";
import PrintedLabelIcon from "./simple-rate-tooltip/printed-label.svg";
import { OutboundRateOption } from "./util";

const getRateMoney = (option: OutboundRateOption | string | null) =>
  typeof option !== "string" && option?.rate !== undefined
    ? toMoney({
        amount: option.rate,
        currency: option.currency,
        struck: option.struck,
      })
    : null;

export const getLabelText = (option: OutboundRateOption | string | null) =>
  typeof option === "string"
    ? option
    : option
      ? getCarrierServiceLabel(option.carrier, option.service)
      : "";

export const getDeliveryText = (option: OutboundRateOption | string | null) => {
  if (typeof option === "string" || !option) {
    return "";
  }
  if (option.estimatedDeliveryDate) {
    const estimatedDeliveryDate = Temporal.PlainDate.from(
      option.estimatedDeliveryDate,
    );
    return `Est. delivery ${formatShortDayDate(estimatedDeliveryDate)}`;
  }
  // Prefer transitDays — it's the carrier's SLA expressed as a count and
  // doesn't drift with quote day. Fall back to deliveryDays for rates that
  // haven't been normalized yet.
  const days = option.transitDays ?? option.deliveryDays;
  if (!days) {
    return "";
  }
  return `Est. transit ${days} day${days === "1" ? "" : "s"}`;
};

const getMarginAmount = (
  totalShippingPaid?: number,
  rate?: number,
  lcgInfo?: LcgInfo,
) => {
  if (!isDef(totalShippingPaid) || !isDef(rate)) {
    return undefined;
  }
  return totalShippingPaid - rate - landedCostMajorUnits(lcgInfo);
};

const SimpleRateTooltipContent = memo(function SimpleRateTooltipContent() {
  return (
    <Flex dir="column" gap="lg">
      <Text fontSize="sm" fontWeight="semibold" textColor="white">
        UPS Simple Rate
      </Text>
      <Flex align="center" dir="row" gap="xl" justify="center">
        <Flex align="center" dir="column" gap="sm">
          <PackageBoxIcon />
          <Text fontSize="xs" textAlign="center" textColor="white">
            Actual weight
          </Text>
        </Flex>
        <FlowArrowIcon />
        <Flex align="center" dir="column" gap="sm">
          <PrintedLabelIcon />
          <Flex align="center" dir="column" gap="none">
            <Text fontSize="xs" textAlign="center" textColor="white">
              Printed weight
            </Text>
            <Text fontSize="xs" textAlign="center" textColor="white">
              may differ
            </Text>
          </Flex>
        </Flex>
      </Flex>
      <Text fontSize="xs" textColor="tooltip-supporting-text">
        UPS Simple Rate uses flat pricing based on package size. The weight
        printed on the label may not match the package’s actual weight. This is
        expected.
      </Text>
      <Divider />
      <Flex align="stretch" dir="row">
        <Flex align="center" dir="row" gap="sm" grow={1} pr="lg">
          <ScalesIcon height={28} width={28} />
          <Flex dir="column" gap="xxs">
            <Text fontSize="xs" textColor="tooltip-supporting-text">
              Max weight
            </Text>
            <Text fontSize="sm" fontWeight="medium" textColor="white">
              50 lb
            </Text>
          </Flex>
        </Flex>
        <Flex
          align="center"
          borderColor="primary"
          borderLeftWidth="1px"
          borderStyle="solid"
          dir="row"
          gap="sm"
          grow={1}
          pl="lg"
        >
          <CubeIcon height={28} width={28} />
          <Flex dir="column" gap="xxs">
            <Text fontSize="xs" textColor="tooltip-supporting-text">
              Max volume
            </Text>
            <Text fontSize="sm" fontWeight="medium" textColor="white">
              1 cu ft
            </Text>
            <Text fontSize="xs" textColor="tooltip-supporting-text">
              (1,728 cu in)
            </Text>
          </Flex>
        </Flex>
      </Flex>
      <Divider />
      <Text
        fontSize="xxs"
        textAlign="center"
        textColor="tooltip-supporting-text"
      >
        Larger or heavier packages use standard UPS rates.
      </Text>
    </Flex>
  );
});

const ShipmentRateSelectionDropdownOption = memo(
  function ShipmentRateSelectionDropdownOption({
    option,
    totalShippingPaid,
    cheapestRate,
    minDeliveryDays,
    hideBadges,
    selected,
  }: {
    option: OutboundRateOption | string | null;
    totalShippingPaid?: number;
    cheapestRate?: number;
    minDeliveryDays?: string;
    hideBadges?: boolean;
    selected?: boolean;
  }) {
    const [overflowRef, setOverflowRef] = useState<HTMLDivElement | null>(null);
    const [titleRef, setTitleRef] = useState<HTMLElement | null>(null);

    const team = useRequiredContext(TeamContext);
    const carrierAccountsContext = useContext(CarrierAccountsContext);

    const carrierAccountName =
      typeof option !== "string" && option?.carrier_account_id
        ? carrierAccountsContext?.getCarrierAccountName(
            option.carrier_account_id,
          )
        : undefined;

    if (option === RedoShopifyDeliveryMethodType.PickUp) {
      return (
        <Flex align="center" dir="row" gap="lg" p="xs">
          <LocalPickupLogo color="ghost" height={14} width={14} />
          <Text fontSize="xs">
            {
              RedoShopifyDeliveryMethodTypeToString[
                option as RedoShopifyDeliveryMethodType
              ]
            }
          </Text>
        </Flex>
      );
    }

    if (option === RedoShopifyDeliveryMethodType.Local) {
      return (
        <Flex align="center" dir="row" gap="lg" p="xs">
          <TruckIcon color="ghost" height={14} width={14} />
          <Text fontSize="xs">
            {
              RedoShopifyDeliveryMethodTypeToString[
                option as RedoShopifyDeliveryMethodType
              ]
            }
          </Text>
        </Flex>
      );
    }

    if (option === RedoShopifyDeliveryMethodType.Freight) {
      return (
        <Flex align="center" dir="row" gap="lg" p="xs">
          <TruckIcon color="ghost" height={14} width={14} />
          <Text fontSize="xs">
            {
              RedoShopifyDeliveryMethodTypeToString[
                option as RedoShopifyDeliveryMethodType
              ]
            }
          </Text>
        </Flex>
      );
    }

    if (option === RedoShopifyDeliveryMethodType.Shipping) {
      return (
        <Flex align="center" dir="row" gap="lg" p="xs">
          <TruckIcon color="ghost" height={14} width={14} />
          <Text fontSize="xs">
            {
              RedoShopifyDeliveryMethodTypeToString[
                option as RedoShopifyDeliveryMethodType
              ]
            }
          </Text>
        </Flex>
      );
    }

    const mainText = getDeliveryText(option)
      ? `${getLabelText(option)} - ${getDeliveryText(option)}`
      : getLabelText(option);
    const lcgInfo = getLcgInfo(option);
    const marginAmount = getMarginAmount(
      totalShippingPaid,
      (option as OutboundRateOption)?.rate,
      lcgInfo,
    );

    const isCheapestRate =
      isDef((option as OutboundRateOption)?.rate) &&
      (option as OutboundRateOption)?.rate === cheapestRate &&
      !(option as OutboundRateOption)?.isExternalBilling;
    const isFastestRate =
      isDef((option as OutboundRateOption)?.deliveryDays) &&
      (option as OutboundRateOption)?.deliveryDays === minDeliveryDays;

    const showMargin = team?.settings?.checkoutOptimization?.enabled;
    const showRateProvider =
      localStorage.getItem("showRateProvider") === "true";

    let rateProvider: string | undefined = (option as OutboundRateOption)
      ?.provider;
    if (rateProvider === RateProvider.INTERNATIONAL_BRIDGE) {
      rateProvider = "IB";
    } else if (rateProvider === RateProvider.REDO_LANDMARK_GLOBAL) {
      rateProvider = "RLG";
    }

    // version is null only when bought rates span mixed versions across groups;
    // in that case show the provider alone rather than a misleading default.
    const rateVersion = (option as OutboundRateOption)?.version;
    const rateProviderBadgeText =
      rateVersion === null
        ? rateProvider
        : `${rateProvider} v${rateVersion ?? 1}`;
    const simpleRateCode =
      typeof option === "string" ? undefined : option?.simpleRateCode;

    const showLcgBreakdown =
      localStorage.getItem("showLandedCostBreakdown") === "true";

    const isExternalBilling = Boolean(
      typeof option !== "string" && option?.isExternalBilling,
    );

    const billingAccount =
      typeof option !== "string" && option?.billingAccountId
        ? carrierAccountsContext?.getBillingAccount(option.billingAccountId)
        : undefined;

    const externalBillingBadgeText =
      billingAccount?.accountName ??
      (billingAccount &&
      billingAccountType(billingAccount) === CarrierBillingAccountType.Receiver
        ? "Receiver billing"
        : "3rd party billing");

    const hasBadges = Boolean(
      (option as OutboundRateOption)?.redoRate ||
      (option as OutboundRateOption)?.provider === RateProvider.TIKTOK ||
      carrierAccountName ||
      isExternalBilling ||
      isCheapestRate ||
      isFastestRate ||
      (showRateProvider && !!rateProvider) ||
      !!simpleRateCode ||
      lcgInfo.showLandedCost,
    );

    const rateMoney = getRateMoney(option);

    return (
      <OverflowTooltip
        overflowRef={overflowRef}
        tooltipProps={{ title: mainText }}
        wrapperStyle={{ flexGrow: 1, minWidth: 0 }}
      >
        <Flex className={styles.selection} dir="column" gap="xs">
          {option ? (
            <>
              {/* Row 1: radio | logo | carrier + days | price/margin */}
              <Flex
                align="center"
                dir="row"
                gap="md"
                grow={1}
                justify="space-between"
                overflow="hidden"
                ref={setOverflowRef}
              >
                {!hideBadges && (
                  <div
                    className={
                      selected ? styles.radioSelected : styles.radioUnselected
                    }
                  />
                )}
                {typeof option !== "string" && (
                  <Flex align="center" shrink={0}>
                    <ShippingLogo
                      carrier={option.carrier}
                      size={LogoSize.MEDIUM}
                    />
                  </Flex>
                )}
                <Flex dir="column" gap="none" grow={1} overflow="hidden">
                  <OverflowTooltip
                    overflowRef={titleRef}
                    tooltipProps={{ title: getLabelText(option) ?? "" }}
                  >
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      overflow="hidden"
                      ref={setTitleRef}
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {getLabelText(option)}
                    </Text>
                  </OverflowTooltip>
                  {hasBadges && (
                    <Flex dir="row" gap="xs" wrap="wrap">
                      {(option as OutboundRateOption).redoRate &&
                        !isExternalBilling && (
                          <RedoBadge size="xs" text="Redo" />
                        )}
                      {(option as OutboundRateOption).provider ===
                        RateProvider.TIKTOK && (
                        <RedoBadge
                          segmentLeading={{ type: "icon", Icon: TiktokIcon }}
                          size="xs"
                          text="TikTok"
                        />
                      )}
                      {isExternalBilling ? (
                        <RedoBadge size="xs" text={externalBillingBadgeText} />
                      ) : (
                        carrierAccountName && (
                          <RedoBadge size="xs" text={carrierAccountName} />
                        )
                      )}
                      {isCheapestRate && (
                        <RedoBadge size="xs" text="Cheapest" />
                      )}
                      {isFastestRate && <RedoBadge size="xs" text="Fastest" />}
                      {showRateProvider && !!rateProvider && (
                        <RedoBadge size="xs" text={rateProviderBadgeText} />
                      )}
                      {!!simpleRateCode && (
                        <Tooltip
                          arrow
                          leftAlign
                          paddingOverride="16px"
                          title={<SimpleRateTooltipContent />}
                        >
                          <Flex w="fit-content">
                            <RedoBadge
                              iconTrailing={{
                                type: "icon",
                                Icon: HelpCircleIcon,
                              }}
                              size="xs"
                              text="Simple Rate"
                            />
                          </Flex>
                        </Tooltip>
                      )}
                    </Flex>
                  )}
                </Flex>
                {!isExternalBilling &&
                  (rateMoney || (showMargin && isDef(marginAmount))) && (
                    <Flex
                      className={styles.priceColumn}
                      dir="column"
                      gap="xxs"
                      justify="center"
                    >
                      {rateMoney && (
                        <Text
                          fontSize="xs"
                          fontWeight="medium"
                          textAlign="right"
                        >
                          <MoneyText
                            source={RateSource.Stripe}
                            value={rateMoney}
                          />
                        </Text>
                      )}
                      {showMargin && isDef(marginAmount) && (
                        <Text
                          fontSize="xxs"
                          textAlign="right"
                          textColor={marginAmount < 0 ? "error" : "success"}
                        >
                          {marginAmount >= 0 ? "+" : ""}
                          <MoneyText
                            source={RateSource.Stripe}
                            value={toMoney({ amount: marginAmount })}
                          />{" "}
                          margin
                        </Text>
                      )}
                    </Flex>
                  )}
              </Flex>
              {/* Row 2: delivery estimate | fees */}
              {!hideBadges &&
                (getDeliveryText(option) || lcgInfo.showLandedCost) && (
                  <Flex
                    align="center"
                    className={styles.deliveryRow}
                    dir="row"
                    justify="space-between"
                  >
                    {getDeliveryText(option) && (
                      <Text fontSize="xxs" textColor="disabled">
                        {getDeliveryText(option)}
                      </Text>
                    )}
                    {lcgInfo.showLandedCost && (
                      <Text fontSize="xxs" textColor="secondary">
                        {"International fees: "}
                        <MoneyText
                          source={RateSource.Stripe}
                          value={toMoney({
                            amount: lcgInfo.totalLandedCost,
                            currency: lcgInfo.currency,
                          })}
                        />
                      </Text>
                    )}
                  </Flex>
                )}
              {lcgInfo.showLandedCost && showLcgBreakdown && (
                <Flex dir="row" gap="xs">
                  <Text fontSize="xxs">
                    {"D: "}
                    <MoneyText
                      source={RateSource.Stripe}
                      value={toMoney({
                        amount: lcgInfo.duties,
                        currency: lcgInfo.currency,
                      })}
                    />
                  </Text>
                  <Text fontSize="xxs">
                    {"T: "}
                    <MoneyText
                      source={RateSource.Stripe}
                      value={toMoney({
                        amount: lcgInfo.taxes,
                        currency: lcgInfo.currency,
                      })}
                    />
                  </Text>
                  <Text fontSize="xxs">
                    {"F: "}
                    <MoneyText
                      source={RateSource.Stripe}
                      value={toMoney({
                        amount: lcgInfo.fees,
                        currency: lcgInfo.currency,
                      })}
                    />
                  </Text>
                </Flex>
              )}
            </>
          ) : (
            <div className={styles.placeholder}>No rate selected</div>
          )}
        </Flex>
      </OverflowTooltip>
    );
  },
);

function outboundRateOptionToId(option: OutboundRateOption | string | null) {
  if (!option) {
    return undefined;
  }
  if (typeof option === "string") {
    return option;
  }

  return `${option.carrier}-${option.service}-${!!option.redoRate}-${option.saturdayDelivery}-${option.carrier_account_id}`;
}

// If a label was purchased outside of redo, but the merchant added manual fulfillment info, the only data we have is the carrier. So show that instead of the entire dropdown
export const SimpleShipmentRateSelectionDropdown = memo(
  function SimpleShipmentRateSelectionDropdown({
    groups,
  }: {
    groups: readonly FulfillmentGroupData[];
  }) {
    const allFreight = groups.every(
      (group) => group.shipmentDetails?.freightShipment,
    );

    if (allFreight) {
      const allScacs = groups
        .map((group) => group.shipmentDetails?.freightShipment?.scac)
        .filter((scac): scac is KnownSCAC => !!scac);

      const uniqueScacs = [...new Set(allScacs)];
      const totalCost = groups.reduce((sum, group) => {
        return (
          sum + parseFloat(group.shipmentDetails?.freightShipment?.cost ?? "0")
        );
      }, 0);
      const freightCurrency = groups.find(
        (group) => group.shipmentDetails?.freightShipment?.currency,
      )?.shipmentDetails?.freightShipment?.currency;
      return (
        <Flex dir="column" gap="sm">
          <Text fontSize="xs">Service</Text>
          {uniqueScacs.length === 1 ? (
            <Flex align="center" dir="row" gap="sm">
              <img
                alt={knownScacToLabel[uniqueScacs[0]!]}
                className={styles.freightLogo}
                src={getFreightLogoUrl(uniqueScacs[0]!)}
              />
              <Text fontSize="xs">{knownScacToLabel[uniqueScacs[0]!]}</Text>
              <Text fontSize="xs">
                -{" "}
                <MoneyText
                  source={RateSource.Stripe}
                  value={toMoney({
                    amount: totalCost,
                    currency: freightCurrency,
                  })}
                />
              </Text>
            </Flex>
          ) : (
            <Text fontSize="xs">
              {uniqueScacs.length === 0 ? "No carrier" : "Mixed carriers"}
            </Text>
          )}
        </Flex>
      );
    }
    const noCarriers = groups.every((group) => !group.fulfillmentInfo?.carrier);

    const carrier = groups.every(
      (group) =>
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        group.fulfillmentInfo?.carrier === groups[0].fulfillmentInfo?.carrier,
    )
      ? // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        groups[0].fulfillmentInfo?.carrier
      : null;

    return (
      <Flex dir="column" gap="sm">
        <Text fontSize="xs">Service</Text>
        {carrier ? (
          <Flex align="center" dir="row" gap="sm">
            <ShippingLogo carrier={carrier} size={LogoSize.MEDIUM} />
            <Text fontSize="xs">{carrierToLabel[carrier]}</Text>
          </Flex>
        ) : (
          <Text fontSize="xs">
            {noCarriers ? "No carrier" : "Mixed carriers"}
          </Text>
        )}
      </Flex>
    );
  },
);

export const ShipmentRateSelectionDropdown = memo(
  function ShipmentRateSelectionDropdown({
    disabled,
    label,
    options,
    selectedRate,
    setSelectedRate,
    deliveryMethod,
    loading,
    mixedRates,
    fullWidth,
    totalShippingPaid,
    includeAlternateDeliveryMethods,
    usedShippingLearning,
    fulfillmentGroups,
  }: {
    disabled?: boolean;
    label?: string;
    options: readonly OutboundRateOption[];
    selectedRate: OutboundRateOption | null;
    mixedRates: boolean;
    deliveryMethod?: RedoShopifyDeliveryMethodType | null;
    setSelectedRate:
      | ((rate: OutboundRateOption | RedoShopifyDeliveryMethodType) => void)
      | ((rate: OutboundRateOption) => void);
    loading?: boolean;
    fullWidth: boolean;
    totalShippingPaid?: number | undefined;
    includeAlternateDeliveryMethods?: boolean;
    usedShippingLearning?: boolean | null;
    fulfillmentGroups?: readonly FulfillmentGroupData[];
  }) {
    const team = useRequiredContext(TeamContext);
    const { executeWithPinCheck } = usePinVerification();

    const onRateChange = ({
      item,
    }: {
      item: RedoListItem<
        string | OutboundRateOption | RedoShopifyDeliveryMethodType
      >;
    }) => {
      executeWithPinCheck(
        () => setSelectedRate(item.value as OutboundRateOption),
        { type: PinVerificationType.SelectRate },
        { title: "Select rate PIN verification" },
      );
    };

    const [dropdownButtonRef, setDropdownButtonRef] =
      useState<HTMLButtonElement | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const saturdayDeliveryOptions = options.filter(
      (option) => option.saturdayDelivery,
    );
    const nonSaturdayDeliveryOptions = options.filter(
      (option) => !option.saturdayDelivery,
    );

    const cheapestRate = options.reduce((min: number | undefined, option) => {
      if (option.rate === undefined) {
        return min;
      }
      if (min === undefined) {
        return option.rate;
      }
      return Number(option.rate) < Number(min) ? option.rate : min;
    }, options[0]?.rate);

    const minDeliveryDays = options.reduce(
      (min: string | undefined, option) => {
        if (option.deliveryDays === undefined) {
          return min;
        }
        if (min === undefined) {
          return option.deliveryDays;
        }
        return Number(option.deliveryDays) < Number(min)
          ? option.deliveryDays
          : min;
      },
      options[0]?.deliveryDays,
    );

    const displayedOption =
      deliveryMethod !== RedoShopifyDeliveryMethodType.Shipping &&
      deliveryMethod
        ? deliveryMethod
        : selectedRate;

    const dropdownOptions: (
      | OutboundRateOption
      | RedoShopifyDeliveryMethodType
    )[] = [
      ...saturdayDeliveryOptions,
      ...nonSaturdayDeliveryOptions,
      ...(includeAlternateDeliveryMethods
        ? [
            RedoShopifyDeliveryMethodType.PickUp,
            RedoShopifyDeliveryMethodType.Local,
            ...(options.length === 0 &&
            deliveryMethod !== RedoShopifyDeliveryMethodType.Shipping
              ? [RedoShopifyDeliveryMethodType.Shipping]
              : []),
            ...(team.settings.outboundLabels?.freightSettings?.freightEnabled &&
            fulfillmentGroups?.every((g) =>
              g.providers?.every((p) =>
                supportedFreightMarketplaces.includes(p),
              ),
            )
              ? [RedoShopifyDeliveryMethodType.Freight]
              : []),
          ]
        : []),
    ];

    const dropdownOptionItems: RedoListItem<
      string | OutboundRateOption | RedoShopifyDeliveryMethodType
    >[] = [...dropdownOptions].map((option) => ({
      id: outboundRateOptionToId(option)!,
      value: option,
      type: "custom",
      selectionVariant: "none",
      render: (
        <ShipmentRateSelectionDropdownOption
          cheapestRate={cheapestRate}
          minDeliveryDays={minDeliveryDays}
          option={option}
          selected={
            outboundRateOptionToId(option) ===
            outboundRateOptionToId(displayedOption)
          }
          totalShippingPaid={totalShippingPaid}
        />
      ),
      useWrapper: true,
    }));

    const hasSaturdayDelivery = useMemo(() => {
      if (!fulfillmentGroups?.length) {
        return false;
      }
      return fulfillmentGroups.some((group) => group.saturdayDelivery === true);
    }, [fulfillmentGroups]);

    const saturdayDeliveryHeader: RedoListItem<
      string | OutboundRateOption | RedoShopifyDeliveryMethodType
    > = {
      id: "saturday-delivery-header",
      value: "saturday-delivery-header",
      type: "section",
      text: "Saturday delivery",
    };

    const nonSaturdayDeliveryHeader: RedoListItem<
      string | OutboundRateOption | RedoShopifyDeliveryMethodType
    > = {
      id: "non-saturday-delivery-header",
      value: "non-saturday-delivery-header",
      type: "section",
      text: "Non-Saturday delivery",
    };

    const finalDropdownItems = [...dropdownOptionItems];
    if (hasSaturdayDelivery && options.length > 0) {
      const nonSaturdayDeliveryIndex = dropdownOptionItems.findIndex(
        (option) => {
          if (typeof option.value === "string") {
            return false;
          }
          return option.value.saturdayDelivery === false;
        },
      );

      if (nonSaturdayDeliveryIndex > 0) {
        finalDropdownItems.splice(
          nonSaturdayDeliveryIndex,
          0,
          nonSaturdayDeliveryHeader,
        );
      }

      finalDropdownItems.unshift(saturdayDeliveryHeader);
    }

    // Check if all groups are in a done status
    const allDone = fulfillmentGroups?.every((group) =>
      isDoneStatus(group.status),
    );

    // Aggregate cell data from all groups
    const aggregatedCellData = useMemo(() => {
      if (!allDone || !fulfillmentGroups?.length) {
        return null;
      }

      const allCellData = fulfillmentGroups.map(getShippingSelectionCellData);

      // Check if all groups have the same carrier and service
      const firstData = allCellData[0];
      if (!firstData) {
        return null;
      }

      const allSame = allCellData.every(
        (data) =>
          data?.carrierName === firstData.carrierName &&
          data?.serviceName === firstData.serviceName,
      );

      if (!allSame) {
        return null; // Mixed carriers/services
      }

      // Aggregate the rate Money (sum across all groups; rates are USD).
      const totalRateMoney = allCellData.reduce<Money | null>((sum, data) => {
        if (!data?.rateMoney) {
          return sum;
        }
        return sum ? moneyAdd(sum, data.rateMoney) : data.rateMoney;
      }, null);

      return {
        carrierName: firstData.carrierName,
        serviceName: firstData.serviceName,
        rateMoney: totalRateMoney ?? firstData.rateMoney,
        deliveryDays: firstData.deliveryDays,
      };
    }, [allDone, fulfillmentGroups]);

    // Check if all groups have the same redoRate and provider
    const aggregatedBoughtRateInfo = useMemo(() => {
      if (!allDone || !fulfillmentGroups?.length) {
        return null;
      }

      const boughtRates = fulfillmentGroups.map(
        (group) => group.shipment?.boughtRate,
      );
      const firstBoughtRate = boughtRates[0];

      if (!firstBoughtRate) {
        return null;
      }

      const allSameRedoRate = boughtRates.every(
        (rate) =>
          !!rate &&
          rate.is_merchant_carrier_account ===
            firstBoughtRate.is_merchant_carrier_account,
      );

      const allSameProvider = boughtRates.every(
        (rate) => rate?.provider === firstBoughtRate.provider,
      );

      const allSameVersion = boughtRates.every(
        (rate) => rate?.version === firstBoughtRate.version,
      );

      const firstSimpleRateCode = firstBoughtRate.simple_rate_code;
      const allSameSimpleRateCode = boughtRates.every(
        (rate) => rate?.simple_rate_code === firstSimpleRateCode,
      );

      const allExternalBilling = boughtRates.every((rate) =>
        isExternalAccountBilling(rate?.payment_option),
      );

      const billingAccountIds = boughtRates.map((rate) =>
        getBillingAccountId(rate?.payment_option),
      );
      const firstBillingAccountId = billingAccountIds[0];
      const allSameBillingAccountId =
        allExternalBilling &&
        billingAccountIds.every((id) => id === firstBillingAccountId);

      return {
        redoRate: allSameRedoRate
          ? !firstBoughtRate.is_merchant_carrier_account
          : false,
        provider: allSameProvider ? firstBoughtRate.provider : undefined,
        // null signals mixed versions → badge omits the version entirely.
        version: allSameVersion ? (firstBoughtRate.version ?? 1) : null,
        simpleRateCode: allSameSimpleRateCode
          ? (firstSimpleRateCode ?? undefined)
          : undefined,
        isExternalBilling: allExternalBilling,
        billingAccountId:
          allSameBillingAccountId && firstBillingAccountId
            ? firstBillingAccountId
            : undefined,
      };
    }, [allDone, fulfillmentGroups]);

    const selectedItem:
      | RedoListItem<
          string | OutboundRateOption | RedoShopifyDeliveryMethodType
        >
      | undefined = useMemo(() => {
      // For done status groups, use the aggregated cell data from fulfillmentInfo
      if (allDone && aggregatedCellData) {
        // Construct an OutboundRateOption from the aggregated cell data
        const rateOption: OutboundRateOption = {
          carrier: aggregatedCellData.carrierName!,
          service: aggregatedCellData.serviceName!,
          rate: aggregatedCellData.rateMoney
            ? aggregatedCellData.rateMoney.amount
            : undefined,
          currency: aggregatedCellData.rateMoney?.currency,
          // Carry the frozen store-currency snapshot so a purchased label's rate
          // renders frozen instead of live-converting.
          struck: aggregatedCellData.rateMoney?.struck,
          deliveryDays: aggregatedCellData.deliveryDays?.toString(),
          redoRate: aggregatedBoughtRateInfo?.redoRate ?? false,
          provider: aggregatedBoughtRateInfo?.provider,
          version: aggregatedBoughtRateInfo?.version,
          simpleRateCode: aggregatedBoughtRateInfo?.simpleRateCode,
          saturdayDelivery: false,
          isExternalBilling: aggregatedBoughtRateInfo?.isExternalBilling,
          billingAccountId: aggregatedBoughtRateInfo?.billingAccountId,
          key: `${aggregatedCellData.carrierName}-${aggregatedCellData.serviceName}`,
        };

        return {
          id: `${aggregatedCellData.carrierName}-${aggregatedCellData.serviceName}`,
          value: rateOption,
          type: "custom",
          useWrapper: true,
          render: (
            <ShipmentRateSelectionDropdownOption
              cheapestRate={cheapestRate}
              hideBadges
              minDeliveryDays={minDeliveryDays}
              option={rateOption}
              totalShippingPaid={totalShippingPaid}
            />
          ),
        };
      }

      if (!displayedOption) {
        return undefined;
      }

      return {
        id: outboundRateOptionToId(displayedOption)!,
        value: displayedOption,
        type: "custom",
        useWrapper: true,
        render: loading ? (
          <SkeletonText classNames={styles.tallSkeletonText} length={80} />
        ) : (
          <ShipmentRateSelectionDropdownOption
            cheapestRate={cheapestRate}
            hideBadges
            minDeliveryDays={minDeliveryDays}
            option={displayedOption}
            totalShippingPaid={totalShippingPaid}
          />
        ),
      };
    }, [
      displayedOption,
      totalShippingPaid,
      loading,
      cheapestRate,
      minDeliveryDays,
      allDone,
      aggregatedCellData,
      aggregatedBoughtRateInfo,
    ]);

    const carrierTrademarkText = useMemo(() => {
      if (!displayedOption) {
        return false;
      }
      return (
        typeof displayedOption === "object" &&
        "carrier" in displayedOption &&
        carrierToTrademarkText[displayedOption.carrier]
      );
    }, [displayedOption]);

    return (
      <>
        <Flex dir="column" gap="sm" grow={fullWidth ? 1 : undefined}>
          {label && (
            <Flex gap="xs">
              <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                {label}
              </Text>
              {usedShippingLearning && <LearningIcon />}
              {selectedRate &&
                deliveryMethod === RedoShopifyDeliveryMethodType.Shipping && (
                  <AutomationInfoIcon
                    selectedShippingRateSetBy={
                      fulfillmentGroups?.[0]?.selectedShippingRateSetBy
                    }
                    selectedShippingRateSetByInfo={
                      fulfillmentGroups?.[0]?.selectedShippingRateSetByInfo
                    }
                  />
                )}
            </Flex>
          )}
          <RedoSingleSelectDropdownInputButton
            className={fullWidth ? styles.fullWidth : undefined}
            disabled={disabled}
            isDropdownOpen={dropdownOpen}
            onClick={() => setDropdownOpen(!dropdownOpen)}
            placeholder={mixedRates ? "{mixed}" : "Select shipment rate"}
            ref={setDropdownButtonRef}
            selectedItem={selectedItem}
            size="xs"
          />
          {typeof displayedOption === "object" &&
            displayedOption !== null &&
            getDeliveryText(displayedOption) && (
              <Text fontSize="xxs" textColor="tertiary">
                {getDeliveryText(displayedOption)}
              </Text>
            )}
          {carrierTrademarkText && (
            <Text fontSize="xxs" textColor="tertiary">
              {carrierTrademarkText}
            </Text>
          )}
        </Flex>
        <RedoListDropdown
          dropdownAnchor={dropdownButtonRef}
          dropdownOpen={dropdownOpen}
          fitToAnchor
          items={finalDropdownItems}
          itemSelected={onRateChange}
          itemsLoading={loading}
          selectedItems={selectedItem?.id}
          setDropdownOpen={setDropdownOpen}
        />
      </>
    );
  },
);
